import { Link } from "react-router-dom";
import { Mail, MapPin, MessageCircle, Phone } from "lucide-react";
import { COMPANY, PHONE_LINK, WHATSAPP_LINK } from "../../lib/constants";
import { trackEvent } from "../../lib/api";
import { usePublicCityLinks } from "../../lib/werkgebied";

const PRODUCTS = [
  ["Kunststof kozijnen", "/kunststof-kozijnen"], ["Aluminium kozijnen", "/aluminium-kozijnen"], ["Schuifpuien", "/schuifpuien"],
  ["Kunststof schuifpui", "/kunststof-schuifpui"], ["Aluminium schuifpui", "/aluminium-schuifpui"], ["Aluminium hefschuifpui", "/aluminium-hefschuifpui"],
  ["Deuren", "/deuren"], ["Grote puien", "/grote-puien"],
  ["Gevelbekleding", "/gevelbekleding"], ["Rolluiken", "/rolluiken"], ["Velux-dakramen", "/velux-dakramen"],
];
const ADVICE = [
  ["Kosten nieuwe kozijnen", "/kosten-kunststof-kozijnen-zuid-holland"], ["Offerte vergelijken", "/offerte-vergelijken"], ["Subsidie (ISDE)", "/isde-subsidie-kozijnen"],
  ["Kunststof of aluminium?", "/aluminium-of-kunststof-kozijnen"], ["HR++ of HR+++ glas", "/hr++-glas-isolatieglas"], ["Werkwijze", "/werkwijze"],
  ["Montage & afwerking", "/montage"], ["Kennisbank", "/blog"], ["Veelgestelde vragen", "/veelgestelde-vragen"],
];
const COMPANY_LINKS = [["Over ons", "/over-ons"], ["Projecten", "/projecten"], ["Contact", "/contact"]];
const tid = (href) => `footer-link-${href.replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`;

const Group = ({ title, links, className = "", cols = false }) => (
  <nav aria-label={title} className={className}>
    <h2 className="text-[13px] font-semibold uppercase tracking-[0.14em] text-ink">{title}</h2>
    <ul className={`mt-4 ${cols ? "grid grid-cols-2 gap-x-6 gap-y-2.5 sm:block sm:space-y-2.5" : "space-y-2.5"}`}>
      {links.map(([label, href]) => (
        <li key={href}><Link to={href} className="text-[14.5px] text-slate-600 transition-colors hover:text-maas" data-testid={href.startsWith("/kunststof-kozijnen-") ? `footer-city-${href.slice(20)}` : tid(href)}>{label}</Link></li>
      ))}
    </ul>
  </nav>
);

const Contact = () => (
  <div className="col-span-2 lg:col-span-4 lg:pr-8" data-testid="footer-company">
    <img src="/logo-maas.svg" alt="Maas Kozijn" width="120" height="41" className="h-9 w-auto" loading="lazy" />
    <p className="mt-4 max-w-sm text-[14.5px] leading-relaxed text-slate-600">Complete nieuwe kunststof en aluminium kozijnen, schuifpuien en deuren, inclusief glas en montage. Ook voor gevelbekleding, rolluiken en Velux-dakramen. Vanuit {COMPANY.locality} voor woningeigenaren in {COMPANY.region}.</p>
    <ul className="mt-5 space-y-2.5 text-[14.5px] text-slate-600">
      <li className="flex gap-2"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-maas" aria-hidden="true" /> Gevestigd in {COMPANY.locality}</li>
      <li><a href={PHONE_LINK} onClick={() => trackEvent("click_phone", { page_type: "footer" })} className="flex gap-2 hover:text-maas" data-testid="footer-phone"><Phone className="mt-0.5 h-4 w-4 text-maas" aria-hidden="true" /> {COMPANY.phoneDisplay}</a></li>
      <li><a href={`mailto:${COMPANY.email}`} onClick={() => trackEvent("click_email", { page_type: "footer" })} className="flex gap-2 hover:text-maas" data-testid="footer-email"><Mail className="mt-0.5 h-4 w-4 text-maas" aria-hidden="true" /> {COMPANY.email}</a></li>
      <li><a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent("click_whatsapp", { page_type: "footer" })} className="flex gap-2 hover:text-maas" data-testid="footer-whatsapp"><MessageCircle className="mt-0.5 h-4 w-4 text-whatsapp" aria-hidden="true" /> WhatsApp</a></li>
    </ul>
    <dl className="mt-5 max-w-[260px] space-y-1 text-[13.5px] text-slate-600" data-testid="footer-opening-hours">
      {COMPANY.openingHours.map(([d, t]) => <div key={d} className="flex justify-between"><dt>{d}</dt><dd>{t}</dd></div>)}
    </dl>
    <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-[14.5px] font-medium text-ink">
      {COMPANY_LINKS.map(([l, h]) => <li key={h}><Link to={h} className="hover:text-maas" data-testid={tid(h)}>{l}</Link></li>)}
    </ul>
  </div>
);

// Werkgebied: alleen gepubliceerde plaatsen uit Beheer → Werkgebied (max. 8), plus het overzicht.
export const SiteFooter = () => {
  const cities = usePublicCityLinks();
  const area = [...cities.slice(0, 8).map((c) => [c.name, `/kunststof-kozijnen-${c.slug}`]), ["Alle plaatsen", "/werkgebied"]];
  return (
    <footer data-testid="site-footer" className="border-t border-slate-200 bg-slate-50">
      <div className="container-tight grid grid-cols-2 gap-x-6 gap-y-10 py-14 lg:grid-cols-12 lg:py-16">
        <Contact />
        <Group title="Producten" links={PRODUCTS} className="lg:col-span-3" />
        <Group title="Advies & informatie" links={ADVICE} className="lg:col-span-3" />
        <Group title="Werkgebied" links={area} cols className="col-span-2 sm:col-span-1 lg:col-span-2" />
      </div>
      <div className="border-t border-slate-200">
        <div className="container-tight flex flex-col gap-3 py-5 pb-24 text-[13px] text-slate-500 md:flex-row md:items-center md:justify-between md:pb-5">
          <div data-testid="footer-legal">
            <p>© {new Date().getFullYear()} Maas Kozijn. Alle rechten voorbehouden.</p>
            <p className="mt-0.5">Maas Kozijn is een handelsnaam van Koning Aannemersbedrijf.</p>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            <Link to="/privacy" className="hover:text-maas" data-testid="footer-link-privacy">Privacy & cookies</Link>
            <button type="button" onClick={() => window.dispatchEvent(new Event("open-cookie-preferences"))} data-testid="footer-cookie-prefs" className="hover:text-maas">Cookievoorkeuren</button>
            <a href="/sitemap.xml" className="hover:text-maas" data-testid="footer-link-sitemap">Sitemap</a>
          </div>
        </div>
      </div>
    </footer>
  );
};
